import * as React from 'react';
import { useState, useEffect } from 'react';

/** Material UI Packages. */
import {
    TextField,
    Autocomplete,
    Box,
    IconButton,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { randomId } from '@mui/x-data-grid-generator';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

/** Redux Packages. */
import { useSelector } from 'react-redux';

/** Internal Components. */
import SectionBox from 'shared/ui/GridBox';
import InputField from 'shared/ui/InputForm';
import CustomSkeleton from 'shared/ui/Skeleton';
import { metaOptions } from '../../../config/dutyMetaData';

/** Common styles */
const COMMON_AUTOCOMPLETE_SX = {
    height: '30px',
    '& .MuiInputBase-input': {
        fontSize: '13px',
        height: '12px',
    },
    '& .MuiInputBase-root': {
        fontSize: '13px',
    },
};

const COMMON_TEXTFIELD_SX = {
    '& .MuiInputLabel-root': {
        fontSize: '13px',
    },
};

const COMMON_NUMBER_FIELD_SX = {
    '& .MuiInputBase-input': {
        fontSize: '13px',
        height: '12px',
    },
    '& .MuiInputBase-root': {
        fontSize: '13px',
    },
};

const ADD_BUTTON_SX = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    bgcolor: '#00647e',
    width: '100%',
    height: '30px',
    borderRadius: '5px',
    color: 'white',
    ':hover': { color: '#003a52', bgcolor: '#005662' },
};

export default function ItemDetailForm({ itemDetail, setItemDetail, loadingState, itemFormulasRows, validator }) {
    const clientList = useSelector((state) => state.clientList);
    const categories = useSelector((state) => state.itemCategories);

    const FIELDS = [
        {
            type: 'autocomplete',
            label: 'Client',
            field: 'il_trade_partner',
            options: clientList,
            getValue: (item, option) => option && item?.id === option.id,
            onSelect: (option) => (option ? { id: option.id, name: option.name } : null),
        },
        {
            type: 'autocomplete',
            label: 'Category',
            field: 'il_duty_item_category',
            options: categories,
            getValue: (item, option) => option && item?.id === option.id,
            onSelect: (option) => (option ? { id: option.id, category_name: option.category_name } : null),
        },
        { type: 'text', label: 'Item Name', field: 'item_name' },
        { type: 'text', label: 'Item Code', field: 'item_code' },
        { type: 'text', label: 'Description', field: 'description', full: true },
        {
            type: 'radio',
            label: 'Tier',
            field: 'tier_required',
            // 이 품목은 신고 상세에서 Tier(S01010 등)를 골라야 하는가. 계산·경고·Update ERP 차단이
            // 이 값 하나를 본다 — 카테고리 이름으로 짐작하지 않는다.
            options: [
                { value: true, label: 'Required' },
                { value: false, label: 'Not required' },
            ],
        },
    ];

    /** Reusable Autocomplete component */
    const renderAutocomplete = ({
        value,
        onChange,
        options,
        label,
        getOptionLabel,
        renderOption,
        sx = {},
        width,
        error,
    }) => (
        <Autocomplete
            value={value}
            onChange={onChange}
            options={options}
            getOptionLabel={getOptionLabel}
            renderOption={renderOption}
            renderInput={(params) => (
                <TextField {...params} label={label} size="small" sx={COMMON_TEXTFIELD_SX} error={error} />
            )}
            sx={{
                ...COMMON_AUTOCOMPLETE_SX,
                ...(width && { width }),
                ...sx,
            }}
        />
    );

    /** Reusable Add Button component */
    const AddButton = ({ onClick }) => (
        <IconButton onClick={onClick} sx={ADD_BUTTON_SX}>
            <AddIcon />
        </IconButton>
    );

    /** Handle change for formula selection. */
    const handleFormulaChange = (index, value) => {
        const newMappings = [...itemDetail.il_duty_item_formula_mapping];
        newMappings[index].il_duty_formula_id = value ? value.id : null;
        setItemDetail((prev) => ({ ...prev, il_duty_item_formula_mapping: newMappings }));
    };

    /** Add new formula mapping to item detail. */
    const handleAddMapping = () => {
        setItemDetail((prev) => ({
            ...prev,
            il_duty_item_formula_mapping: [
                ...prev.il_duty_item_formula_mapping,
                { id: randomId(), il_duty_item_id: itemDetail.id, il_duty_formula_id: null },
            ],
        }));
    };

    /** Remove formula mapping from item detail. */
    const handleRemoveMapping = (index) => {
        const newMappings = itemDetail.il_duty_item_formula_mapping.filter((_, i) => i !== index);
        setItemDetail((prev) => ({ ...prev, il_duty_item_formula_mapping: newMappings }));
    };

    /** Get available formulas for the current index. */
    const getAvailableFormulas = (currentIndex) => {
        const selectedIds = itemDetail?.il_duty_item_formula_mapping
            .filter((_, i) => i !== currentIndex)
            .map((m) => m.il_duty_formula_id)
            .filter(Boolean);
        return itemFormulasRows?.filter((f) => !selectedIds.includes(f.id)) || [];
    };

    /** Handle change for item info fields. */
    const handleChange = (field, value) => {
        setItemDetail((prev) => ({ ...prev, [field]: value }));
    };

    /** Render field for item info. */
    const renderField = (config) => {
        const { type, label, field, options, getValue, onSelect } = config;
        const value = itemDetail?.[field];
        const error = validator[field];

        if (type === 'radio') {
            return (
                <FormControl key={field} size="small" sx={{ px: 0.5 }}>
                    <FormLabel sx={{ fontSize: '12px' }}>{label}</FormLabel>
                    <RadioGroup
                        row
                        name={field}
                        value={value ? 'true' : 'false'}
                        onChange={(e) => handleChange(field, e.target.value === 'true')}
                    >
                        {options.map((option) => (
                            <FormControlLabel
                                key={String(option.value)}
                                value={String(option.value)}
                                control={<Radio size="small" />}
                                label={option.label}
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '13px' } }}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );
        }

        if (type === 'autocomplete') {
            const selected = options.find((opt) => getValue?.(value, opt)) || null;

            return (
                <Autocomplete
                    key={field}
                    options={options}
                    value={selected}
                    getOptionLabel={(o) => o.name || o.category_name || ''}
                    renderOption={(props, option) => {
                        const { key, ...rest } = props;
                        return (
                            <li {...rest} key={key} style={{ fontSize: '13px' }}>
                                {option.name || option.category_name}
                            </li>
                        );
                    }}
                    onChange={(_, v) => handleChange(field, onSelect(v))}
                    renderInput={(params) => (
                        <TextField {...params} label={label} size="small" sx={COMMON_TEXTFIELD_SX} error={error} />
                    )}
                    sx={COMMON_AUTOCOMPLETE_SX}
                />
            );
        }

        return (
            <InputField
                key={field}
                label={label}
                value={value || ''}
                onChange={(e) => handleChange(field, e.target.value)}
                inputStyle={{ width: '100%' }}
                error={error}
            />
        );
    };

    /** Handle change for meta key. */
    const handleKeyChange = (index, value) => {
        const newMetas = [...itemDetail.il_duty_item_meta];
        newMetas[index].meta_key = value ? value.key : '';
        setItemDetail((prev) => ({ ...prev, il_duty_item_meta: newMetas }));
    };

    /** Handle change for meta value. */
    const handleValueChange = (index, value) => {
        const newMetas = [...itemDetail.il_duty_item_meta];
        newMetas[index].meta_value = value === '' ? '' : parseFloat(value);
        setItemDetail((prev) => ({ ...prev, il_duty_item_meta: newMetas }));
    };

    /** Add new meta to item detail. */
    const handleAddMeta = () => {
        setItemDetail((prev) => ({
            ...prev,
            il_duty_item_meta: [
                ...prev.il_duty_item_meta,
                { id: randomId(), il_duty_item_id: itemDetail.id, meta_key: '', meta_value: '' },
            ],
        }));
    };

    /** Remove meta from item detail. */
    const handleRemoveMeta = (index) => {
        const newMetas = itemDetail.il_duty_item_meta.filter((_, i) => i !== index);
        setItemDetail((prev) => ({ ...prev, il_duty_item_meta: newMetas }));
    };

    /** Render loading state. */
    if (loadingState.itemList || loadingState.itemCategories || loadingState.clientList) {
        return (
            <Box sx={{ width: '100%', height: '100%', p: 2 }}>
                <CustomSkeleton />
            </Box>
        );
    }

    return (
        <>
            <Grid container spacing={2}>
                <SectionBox title="Item Info" size={4}>
                    {FIELDS.map(renderField)}
                </SectionBox>

                <SectionBox title="Item Metadata" size={4}>
                    {itemDetail?.il_duty_item_meta?.map((meta, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={0.5} sx={{ width: '100%' }}>
                            {renderAutocomplete({
                                value: metaOptions.find((opt) => opt.key === meta.meta_key) || null,
                                onChange: (_, newValue) => handleKeyChange(index, newValue),
                                options: metaOptions,
                                label: 'Type',
                                getOptionLabel: (option) => option.label,
                                renderOption: (props, option) => {
                                    const { key, ...rest } = props;
                                    return (
                                        <li {...rest} key={key}>
                                            <span style={{ fontSize: '13px' }}>{option.label}</span>
                                        </li>
                                    );
                                },
                                width: '50%',
                            })}

                            <TextField
                                label="Value"
                                size="small"
                                type="number"
                                value={meta.meta_value}
                                onChange={(e) => handleValueChange(index, e.target.value)}
                                sx={{
                                    width: '50%',
                                    height: '30px',
                                    ...COMMON_NUMBER_FIELD_SX,
                                }}
                            />

                            <IconButton onClick={() => handleRemoveMeta(index)}>
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    ))}

                    <AddButton onClick={handleAddMeta} />
                </SectionBox>

                <SectionBox title="Item Formula" size={4}>
                    {itemDetail?.il_duty_item_formula_mapping?.map((map, index) => {
                        const selectedFormula = itemFormulasRows?.find((f) => f.id === map.il_duty_formula_id) || null;

                        return (
                            <Box key={index} display="flex" alignItems="center">
                                {renderAutocomplete({
                                    value: selectedFormula,
                                    onChange: (event, newValue) => handleFormulaChange(index, newValue),
                                    options: getAvailableFormulas(index),
                                    label: 'Formula',
                                    getOptionLabel: (option) => `${option.duty_name} | ${option.formula}`,
                                    renderOption: (props, option) => {
                                        const { key, id, ...rest } = props;
                                        return (
                                            <li {...rest} key={id}>
                                                <span style={{ fontSize: '13px' }}>
                                                    {option.duty_name} | {option.formula} | {option.description}
                                                </span>
                                            </li>
                                        );
                                    },
                                    width: '100%',
                                })}

                                <IconButton onClick={() => handleRemoveMapping(index)}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        );
                    })}

                    <AddButton onClick={handleAddMapping} />
                </SectionBox>
            </Grid>
        </>
    );
}
